import { z } from 'zod';

export const externalMessageStatusValues = [
  'created',
  'pending',
  'sent',
  'failed',
  'declined',
  'accepted',
  'expired',
] as const;
export const externalMessageStatusSchema = z.enum(externalMessageStatusValues);
export type ExternalMessageStatus = z.infer<typeof externalMessageStatusSchema>;

// Mirrors the legacy `GetAllMessagesDto.status` filter — `'all'` means no
// status filter, `'to_be_send'` is a virtual bucket meaning `status='created'
// AND toBeSendAt > now()`. Train B leaves the runtime semantics for Train C
// but exposes the filter shape so the FE can use it once it lands.
export const externalMessagesFilterStatusValues = [
  'all',
  'created',
  'pending',
  'sent',
  'failed',
  'declined',
  'accepted',
  'expired',
  'to_be_send',
] as const;
export const externalMessagesFilterStatusSchema = z.enum(
  externalMessagesFilterStatusValues
);
export type ExternalMessagesFilterStatus = z.infer<
  typeof externalMessagesFilterStatusSchema
>;

export const externalMessageItemSchema = z.object({
  id: z.uuid(),
  templateId: z.uuid(),
  recipient: z.string(),
  itemId: z.uuid(),
  senderId: z.uuid(),
  // Null once the receiving tour operator is deleted — the FK sets it null
  // and the row survives as history.
  receiverId: z.uuid().nullable(),
  status: externalMessageStatusSchema,
  retryCount: z.number().int().nonnegative(),
  error: z.unknown().nullable(),
  createdOn: z.string(),
  updatedOn: z.string().nullable(),
  sentAt: z.string().nullable(),
  acknowledgedAt: z.string().nullable(),
  expireAt: z.string().nullable(),
  toBeSendAt: z.string().nullable(),
  resendFromId: z.uuid().nullable(),
  isAutomated: z.boolean(),
});
export type ExternalMessageItem = z.infer<typeof externalMessageItemSchema>;

// Enriched row for the external-message dashboard — the base message plus the
// joined receiver (tour operator) name/type, the operation-item title, and the
// message-template title. Mirrors the columns the legacy `OperationsMessagesPage`
// rendered.
export const externalMessageListItemSchema = externalMessageItemSchema.extend({
  receiverName: z.string().nullable(),
  receiverType: z.string().nullable(),
  itemTitle: z.unknown().nullable(),
  templateTitle: z.string().nullable(),
  // Operation-item status chip (legacy "Operation item" column) + the itinerary
  // id for the item deep-link.
  operationStatusTitle: z.string().nullable(),
  operationStatusColor: z.string().nullable(),
  itineraryId: z.uuid().nullable(),
  // Item start-location IANA timezone (from `legs[0].details.startLocation`).
  // Powers the office-time hover tooltip on the time columns — legacy
  // `OperationsMessagesPage` read the same `legs[0]…timezone` for "(Local)".
  startLocationTimezone: z.string().nullable(),
  // Live service start from the joined itinerary item (TCO-OPS09-055 /
  // TP-4060). After Planner "Add day before" shifts item dates, the messages
  // list re-reads this — not a date frozen inside the message content.
  itemStartTime: z.string().nullable(),
  /**
   * Service details that have changed since this message was delivered: the
   * delivered `content` snapshot diffed against a fresh render of the item
   * (`external-messages/stale-service-details.ts`). Empty means the recipient's
   * copy still matches XT — or that there is nothing to compare (not yet sent,
   * no snapshot), which is never reported as changed.
   *
   * Non-empty means the recipient is holding out-of-date instructions and the
   * operator must decide to resend; XT never re-sends on an ordinary edit.
   */
  staleServiceFields: z
    .array(
      z.object({
        /** Twilio content-variable name, e.g. `startLocationTitle`. */
        key: z.string(),
        /** The value the recipient is holding. */
        was: z.string(),
        /** What the item would render today. */
        now: z.string(),
      })
    )
    .default([]),
});
export type ExternalMessageListItem = z.infer<
  typeof externalMessageListItemSchema
>;

export const createExternalMessageInput = z.object({
  templateId: z.uuid(),
  itemId: z.uuid(),
  recipient: z.string().min(1),
  receiverId: z.uuid(),
  toBeSendAt: z.string().nullable().optional(),
  expireAt: z.string().nullable().optional(),
  isAutomated: z.boolean().optional(),
});
export type CreateExternalMessageInput = z.infer<
  typeof createExternalMessageInput
>;

export const findExternalMessagesInput = z.object({
  offset: z.number().int().nonnegative().optional(),
  limit: z.number().int().min(1).max(200).optional(),
  search: z.string().optional(),
  status: externalMessagesFilterStatusSchema.optional(),
  receiverIds: z.array(z.uuid()).optional(),
  // Legacy `receiverTypes` facet — filters by tour-operator type (driver/guide).
  receiverTypes: z.array(z.string()).optional(),
  templateIds: z.array(z.uuid()).optional(),
  /**
   * XT-380 Messages tab: virtual bucket — content JSON serialised length ≥ 1024
   * (WhatsApp field cap). Same threshold as operations `messageStatuses: max_chars`.
   */
  maxChars: z.boolean().optional(),
  /**
   * XT-380 Messages tab: keep the messages entity type while scoping to items
   * whose org-scoped operation_status.status_code is in this list (e.g. `S0`
   * for Modified).
   */
  operationStatusCodes: z.array(z.string().min(1)).max(20).optional(),
  orderBy: z
    .enum(['createdOn', 'sentAt', 'status', 'recipient', 'acknowledgedAt'])
    .optional(),
  order: z.enum(['asc', 'desc']).optional(),
});
export type FindExternalMessagesInput = z.infer<
  typeof findExternalMessagesInput
>;

export const findExternalMessagesResultSchema = z.object({
  data: z.array(externalMessageListItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});
export type FindExternalMessagesResult = z.infer<
  typeof findExternalMessagesResultSchema
>;

export const externalMessageFailedCountsSchema = z.object({
  count: z.number().int().nonnegative(),
});
export type ExternalMessageFailedCounts = z.infer<
  typeof externalMessageFailedCountsSchema
>;

export const sendExternalMessageNowInput = z.object({ id: z.uuid() });
export type SendExternalMessageNowInput = z.infer<
  typeof sendExternalMessageNowInput
>;

export const resendExternalMessageInput = z.object({ id: z.uuid() });
export type ResendExternalMessageInput = z.infer<
  typeof resendExternalMessageInput
>;
